import LeaderboardApi from '@/api/leaderboard'
import { isFeatureEnabled, getVariant } from './feature-toggles.module'

const _state = {
  isLeaderboardVisible: false,
}

const MUTATION_TYPES = {
  TOGGLE_LEADERBOARD_VISIBILITY: 'TOGGLE_LEADERBOARD_VISIBILITY',
}

const UNLEASH_FLAG_NEW_LEADERBOARD = 'AHA-42758-new-leaderboard'

const _getters = {
  // embedApp/isScoredSlide is the source of truth; here we additionally honour the
  // new-leaderboard rollout gate — plugin scored slides only count once the flag is on.
  isQuizSlide: (_, getters, __, rootGetters) => (slide) => {
    if (!slide) return false
    if (slide.type === 'pickAnswer') return true
    if (!getters.canUseNewLeaderboard) return false
    return rootGetters['embedApp/isScoredSlide'](slide)
  },

  quizSlideIdsUpTo: (_, getters, rootState) => (order) => (rootState.slides || [])
    .filter((slide) => getters.isQuizSlide(slide)
      && (order === undefined || slide.order <= order))
    .sort((a, b) => a.order - b.order)
    .map((slide) => Number(slide.id)),

  canUseNewLeaderboard(_, __, rootState, rootGetters) {
    if (!isFeatureEnabled(rootGetters, UNLEASH_FLAG_NEW_LEADERBOARD)) return false
    const turnOnDate = getVariant(rootGetters, UNLEASH_FLAG_NEW_LEADERBOARD)?.payload?.value
    if (!turnOnDate || !rootState.presentation?.createdAt) return false
    return new Date(rootState.presentation.createdAt) > new Date(turnOnDate)
  },

  isLeaderboardVisible(state, getters) {
    if (!getters.canUseNewLeaderboard) return false
    return state.isLeaderboardVisible
  },
}

const actions = {
  // show/hide the leaderboard during the quiz
  subscribeLeaderboardVisibilityAction: async ({ getters, dispatch }, { presentation }) => {
    if (!getters.canUseNewLeaderboard) return
    dispatch('kvSync/subscribeKeyAction', {
      key: `/leaderboards/${presentation.id}`,
    }, { root: true })
  },

  fetchLeaderboardSlideTopNAction: async ({ rootState, getters }, { subject, order, n = 100 } = {}) => {
    const slideIds = getters.quizSlideIdsUpTo(order)
    return LeaderboardApi.getLeaderboardSlideTopN({
      presentationId: rootState.presentation.id,
      presentationVersion: rootState.presentation.version,
      subject,
      teamScoringRule: rootState.presentation.teamScoringRule,
      slideIds,
      lastSlideId: slideIds.length >= 2 ? slideIds[slideIds.length - 2] : undefined,
      n,
    })
  },

  fetchLeaderboardSlideAroundAction: async ({ rootState, getters }, {
    subject, subjectId, order, k = 10,
  } = {}) => {
    const slideIds = getters.quizSlideIdsUpTo(order)
    return LeaderboardApi.getLeaderboardSlideAround({
      presentationId: rootState.presentation.id,
      presentationVersion: rootState.presentation.version,
      subject,
      subjectId: subjectId || rootState.audienceId,
      teamScoringRule: rootState.presentation.teamScoringRule,
      slideIds,
      k,
    })
  },
}

const mutations = {
  [MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY](state, isVisible) {
    if (typeof isVisible !== 'boolean') {
      state.isLeaderboardVisible = !state.isLeaderboardVisible
      return
    }
    state.isLeaderboardVisible = isVisible
  },
}

const plugins = {
  updateLeaderboardVisibility: (store) => {
    store.subscribeAction((action) => {
      if (action.type === 'kvSync/updateKeyValueAction') {
        const { key, value } = action.payload
        if (key.includes('/leaderboards/')) {
          store.commit(`leaderboard/${MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY}`, JSON.parse(value).isVisible)
        }
      }
    })
  },
}

const leaderboardModule = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
  plugins,
}

export default leaderboardModule
